import { effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { ENVIRONMENT } from '../config/environment.token';
import { ProfileService } from '../profile/profile.service';

import type { IntercomBootSettings, IntercomFn, IntercomUpdateSettings } from './intercom.types';

/**
 * Loads the Intercom widget and keeps identity in sync with Auth0 +
 * the Growzz profile. Mirrors legacy `app.config.js` boot/update calls.
 *
 * `custom_launcher_selector: '#help_link'` wires the top-nav "Message
 * support" item as the default messenger launcher. SKU screens use a
 * separate `#report-data-issue` button that Intercom automations hook
 * onto in the product dashboard — that element id is fixed in the UI.
 *
 * On compact viewports the default launcher sits over bottom-sheet
 * footers (e.g. Plan information Close). While any `aria-modal` dialog
 * is open below the md breakpoint, the launcher is hidden via
 * `hide_default_launcher`; the top-nav help link still opens the messenger.
 */
@Injectable({ providedIn: 'root' })
export class IntercomService {
  private readonly env = inject(ENVIRONMENT);
  private readonly auth = inject(AuthService);
  private readonly profile = inject(ProfileService);
  private readonly router = inject(Router);

  private readonly navigationEnd = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ),
  );

  private booted = false;
  private scriptRequested = false;
  private launcherHidden = false;
  private launcherSyncScheduled = false;
  private compactViewport: MediaQueryList | null = null;
  private dialogObserver: MutationObserver | null = null;

  constructor() {
    effect(() => {
      if (!this.navigationEnd()) return;
      this.update();
    });
  }

  init(): void {
    if (typeof window === 'undefined') return;
    this.ensureStub();
    this.loadScript();
    this.boot();
    this.watchModalLauncher();
  }

  update(): void {
    if (typeof window === 'undefined' || !this.booted) return;
    window.Intercom?.('update');
  }

  syncUser(): void {
    if (typeof window === 'undefined') return;

    if (!this.auth.isAuthenticated()) {
      if (this.booted) {
        window.Intercom?.('shutdown');
        this.booted = false;
        this.launcherHidden = false;
      }
      return;
    }

    if (!this.booted) {
      this.boot();
      return;
    }

    const payload = this.buildUserPayload();
    window.Intercom?.('update', payload);
    this.syncLauncherForModals();
  }

  private boot(): void {
    const settings: IntercomBootSettings = {
      app_id: this.env.intercomAppId,
      custom_launcher_selector: '#help_link',
    };
    window.Intercom?.('boot', settings);
    this.booted = true;
    const payload = this.buildUserPayload();
    if (Object.keys(payload).length > 0) {
      window.Intercom?.('update', payload);
    }
    this.syncLauncherForModals();
  }

  private watchModalLauncher(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    this.compactViewport = window.matchMedia('(width < 48rem)');
    this.compactViewport.addEventListener('change', () => this.syncLauncherForModals());

    this.dialogObserver = new MutationObserver(() => this.syncLauncherForModals());
    this.dialogObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal'],
    });

    this.syncLauncherForModals();
  }

  private syncLauncherForModals(): void {
    if (typeof window === 'undefined' || this.launcherSyncScheduled) return;

    this.launcherSyncScheduled = true;
    requestAnimationFrame(() => {
      this.launcherSyncScheduled = false;
      this.applyLauncherVisibility();
    });
  }

  private applyLauncherVisibility(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !this.booted) {
      return;
    }

    const hide =
      (this.compactViewport?.matches ?? false) &&
      document.querySelector('[aria-modal="true"]') !== null;

    if (hide === this.launcherHidden) return;

    this.launcherHidden = hide;
    window.Intercom?.('update', { hide_default_launcher: hide });
  }

  private buildUserPayload(): IntercomUpdateSettings {
    const user = this.profile.profile();
    if (user) {
      const name = [user.firstName, user.lastName]
        .map((part) => part?.trim())
        .filter((part): part is string => !!part)
        .join(' ');
      return {
        user_id: String(user.id),
        email: user.email,
        ...(name ? { name } : {}),
        ...(user.jobTitle ? { job_title: user.jobTitle } : {}),
        ...(user.growzzRole ? { growzz_role: user.growzzRole } : {}),
      };
    }

    const email = this.auth.email();
    return email ? { email } : {};
  }

  private ensureStub(): void {
    if (typeof window.Intercom === 'function') return;

    const queue: unknown[][] = [];
    const stub = ((...args: unknown[]) => {
      queue.push(args);
    }) as IntercomFn;
    stub.q = queue;
    stub.c = (args: unknown[]) => {
      queue.push(args);
    };
    window.Intercom = stub;
  }

  private loadScript(): void {
    if (this.scriptRequested || typeof document === 'undefined') return;
    this.scriptRequested = true;

    const appId = this.env.intercomAppId;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    document.head.appendChild(script);
  }
}
